function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : charts
// =============================================================================
// ---Third-party imports
import React, { Component } from 'react';
import { Grid, Segment } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { each, filter, findIndex, get, isEmpty, map, orderBy } from 'ldash';
import { useState, useChunk } from 'uix/hooks';
// --- Local imports
import styles from 'uix/styles';
import chartStyle from './charts.scss';
import clsx from 'clsx';
import { default as AutoSizer } from './autosizer/AutoSizer';
// XXX: transform this to functional comp
class PieChartComp extends Component {
    static getDerivedStateFromProps(nextProps, prevState) {
        let realData = [];
        if (nextProps.limit) {
            realData = PieChartComp.getLimitedElems(nextProps.data, nextProps.limit, nextProps.restModifier);
        } else {
            realData = nextProps.data;
        }
        // to be sure the chart will render even if no item has values, set a fake value
        // and ignore it later on
        const filteredByValue = filter(realData, (d)=>d.value);
        if (isEmpty(filteredByValue)) {
            realData = map(realData, (d)=>({
                    ...d,
                    value: 1,
                    fakeValue: true
                }));
        }
        let overall = 0;
        realData.forEach((d)=>{
            overall += d.value;
        });
        return {
            data: realData,
            overall
        };
    }
    render() {
        const { noData, aspect = 16.0 / 13.0 } = this.props;
        const { data, activeIndex, overall } = this.state;
        const activeData = get(data, `${activeIndex}`, null);
        const { ResponsiveContainer, PieChart, Pie, Label, Cell } = this.props.Recharts;
        if ((!data || isEmpty(data)) && noData) {
            return noData;
        }
        // isAnimationActive=false because custom label is not always rendered
        // => https://github.com/recharts/recharts/issues/929
        return /*#__PURE__*/ _jsx(Grid, {
            children: /*#__PURE__*/ _jsxs(Grid.Row, {
                children: [
                    /*#__PURE__*/ _jsx(Grid.Column, {
                        width: 7,
                        children: this.renderLegend()
                    }),
                    /*#__PURE__*/ _jsx(Grid.Column, {
                        width: 9,
                        children: /*#__PURE__*/ _jsx(ResponsiveContainer, {
                            width: "100%",
                            aspect: aspect,
                            children: /*#__PURE__*/ _jsx(PieChart, {
                                children: /*#__PURE__*/ _jsxs(Pie, {
                                    activeIndex: activeIndex,
                                    activeShape: this.renderActiveShape,
                                    data: data,
                                    dataKey: "value",
                                    nameKey: "name",
                                    outerRadius: "90%",
                                    innerRadius: "80%",
                                    onMouseEnter: this.onPieEnter,
                                    onMouseLeave: ()=>this.setState({
                                            activeIndex: null
                                        }),
                                    isAnimationActive: false,
                                    children: [
                                        data.map((entry, index)=>/*#__PURE__*/ _jsx(Cell, {
                                                fill: entry.color
                                            }, `cell-${index}`)),
                                        activeData && /*#__PURE__*/ _jsx(Label, {
                                            value: `${(activeData.value / overall * 100).toFixed(2)}%`,
                                            position: "center",
                                            style: {
                                                fontSize: '2vw',
                                                fontWeight: 'bold',
                                                fill: activeData.color
                                            }
                                        })
                                    ]
                                })
                            })
                        })
                    })
                ]
            })
        });
    }
    constructor(props){
        super(props), _define_property(this, "onPieEnter", (data, index)=>this.setState({
                activeIndex: index
            })), _define_property(this, "renderActiveShape", ({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill })=>{
            const { Sector } = this.props.Recharts;
            return /*#__PURE__*/ _jsxs("g", {
                children: [
                    /*#__PURE__*/ _jsx(Sector, {
                        cx: cx,
                        cy: cy,
                        innerRadius: innerRadius,
                        outerRadius: outerRadius,
                        startAngle: startAngle,
                        endAngle: endAngle,
                        fill: fill
                    }),
                    /*#__PURE__*/ _jsx(Sector, {
                        cx: cx,
                        cy: cy,
                        startAngle: startAngle,
                        endAngle: endAngle,
                        innerRadius: outerRadius + 2,
                        outerRadius: outerRadius + 4,
                        fill: fill
                    })
                ]
            });
        }), _define_property(this, "renderLegend", ()=>{
            const { legendValueModifier } = this.props;
            const { activeIndex, data, overall } = this.state;
            return /*#__PURE__*/ _jsx(_Fragment, {
                children: data.map((item, index)=>{
                    const active = index === activeIndex || activeIndex === null;
                    return /*#__PURE__*/ _jsxs("div", {
                        style: {
                            paddingTop: '1rem',
                            opacity: active ? 1 : 0.3,
                            fontSize: '14px'
                        },
                        className: chartStyle,
                        onMouseEnter: ()=>this.setState({
                                activeIndex: index
                            }),
                        onMouseLeave: ()=>this.setState({
                                activeIndex: null
                            }),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                style: {
                                    display: 'flex',
                                    flexDirection: 'row'
                                },
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        style: {
                                            marginRight: '.5rem'
                                        },
                                        children: item.fakeValue ? 0 : legendValueModifier && legendValueModifier(item.value) || item.value
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: styles.cut,
                                        style: {
                                            flex: 1,
                                            textAlign: 'right',
                                            maxWidth: '1000px'
                                        },
                                        children: /*#__PURE__*/ _jsx("span", {
                                            title: item.name,
                                            children: item.name
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                style: {
                                    borderRadius: '3px',
                                    background: 'rgba(0, 0, 0, 0.05)'
                                },
                                children: /*#__PURE__*/ _jsx("div", {
                                    style: {
                                        width: `${(item.value / overall * 100).toFixed(2)}%`,
                                        height: '6px',
                                        marginTop: '.3rem',
                                        borderRadius: '3px',
                                        background: item.color || 'black'
                                    }
                                })
                            })
                        ]
                    }, item.name);
                })
            });
        });
        this.state = {
            activeIndex: null,
            data: [],
            overall: 0
        };
    }
}
_define_property(PieChartComp, "getLimitedElems", (data, limit, restModifier)=>{
    let pieData = [];
    if (!isEmpty(data)) {
        let ordered = orderBy(data, (e)=>e.value);
        ordered = ordered.reverse();
        let realElems = [];
        let restValue = 0;
        if (ordered.length > limit) {
            realElems = ordered.slice(0, limit);
            each(ordered.slice(limit, ordered.length), (e)=>{
                restValue += e.value;
            });
        } else {
            realElems = ordered;
        }
        pieData = map(realElems, (e)=>({
                ...e
            }));
        if (ordered.length > limit) {
            pieData.push({
                name: 'Rest',
                value: restModifier ? restModifier(restValue) : restValue,
                color: '#bbb'
            });
        }
    }
    return pieData;
});
const PieChart = (props)=>{
    let Recharts = useChunk('Recharts');
    if (!Recharts) return null;
    props = {
        ...props,
        Recharts
    };
    return /*#__PURE__*/ _jsx(PieChartComp, {
        ...props
    });
};
const BarChart = ({ data, chartType, aspect = 16.0 / 10.0, tooltipValueModifier })=>{
    let [showLegend, setShowLegend] = useState(false);
    let [hoverIndex, setHoverIndex] = useState(null);
    let Recharts = useChunk('Recharts');
    if (!Recharts) return null;
    let { ResponsiveContainer, BarChart, Bar, LineChart, Line, YAxis, XAxis, Tooltip, Legend, CartesianGrid, Cell } = Recharts;
    // <button onClick={() => setShowLegend(!showLegend)}>
    //   toggle legend
    // </button>
    let xAxis = /*#__PURE__*/ _jsx(XAxis, {
        dataKey: "x",
        label: {
            value: '',
            position: 'insideBottomRight',
            offset: 0
        },
        dy: 15,
        fontSize: 10,
        angle: -45
    });
    let yAxis = /*#__PURE__*/ _jsx(YAxis, {
        yAxisId: "yAxis",
        fontSize: 12,
        stroke: "var(--vl_accent_color)"
    });
    let toolTip = /*#__PURE__*/ _jsx(Tooltip, {
        content: /*#__PURE__*/ _jsx(BarChartTooltip, {
            valueModifier: tooltipValueModifier
        }),
        cursor: false
    });
    let legend = /*#__PURE__*/ _jsx(Legend, {
        wrapperStyle: {
            visibility: showLegend ? 'visible' : 'hidden',
            top: '35px',
            left: 0,
            width: '200px',
            background: 'var(--vl_accent_color)',
            borderRadius: '3px',
            border: '1px solid #789'
        }
    });
    let grid = /*#__PURE__*/ _jsx(CartesianGrid, {
        stroke: "var(--vl_borderColor)"
    });
    let chart = null;
    if (chartType === 'line') {
        chart = /*#__PURE__*/ _jsxs(LineChart, {
            width: 100,
            height: 32,
            data: data,
            children: [
                xAxis,
                yAxis,
                toolTip,
                legend,
                grid,
                /*#__PURE__*/ _jsx(Line, {
                    type: "monotone",
                    dataKey: "y",
                    yAxisId: "yAxis",
                    stroke: "var(--vl_accent_color)",
                    strokeWidth: 2,
                    dot: false
                })
            ]
        });
    } else if (chartType === 'bar') {
        chart = /*#__PURE__*/ _jsxs(BarChart, {
            data: data,
            margin: {
                top: 20,
                right: 40,
                bottom: 30,
                left: 20
            },
            children: [
                xAxis,
                yAxis,
                toolTip,
                legend,
                grid,
                /*#__PURE__*/ _jsx(Bar, {
                    yAxisId: "yAxis",
                    dataKey: "y",
                    barSize: 20,
                    onMouseEnter: (e, i)=>setHoverIndex(i),
                    onMouseLeave: ()=>setHoverIndex(null),
                    children: data.map((entry, index)=>/*#__PURE__*/ _jsx(Cell, {
                            fill: index === hoverIndex ? 'var(--vl_accent_color-hover)' : 'var(--vl_accent_color)'
                        }, `cell-${entry.date}`))
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(ResponsiveContainer, {
        width: "100%",
        aspect: aspect,
        children: chart
    });
};
const BarChartTooltip = ({ active, payload, label, valueModifier })=>{
    if (active) {
        const value = get(payload, [
            '0',
            'value'
        ], '');
        return /*#__PURE__*/ _jsx(Segment, {
            raised: true,
            children: `${label}: ${valueModifier ? valueModifier(value) : value}`
        });
    }
    return null;
};
const AreaChartTooltip = ({ active, payload, label, tooltip })=>{
    if (active) {
        const values = [
            get(payload, [
                '0',
                'value'
            ], ''),
            get(payload, [
                '1',
                'value'
            ], '')
        ];
        const { valueModifiers, labels } = tooltip;
        let a = 0;
        let b = 1;
        if (values[a] < values[b]) {
            a = 1;
            b = 0;
        }
        return /*#__PURE__*/ _jsxs(Segment, {
            raised: true,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        fontWeight: 'bold'
                    },
                    children: label
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: clsx(chartStyle.areaChartTooltipLabel, a == 0 ? chartStyle.left : chartStyle.right),
                    children: `${valueModifiers[a] ? valueModifiers[a](values[a]) : values[a]} ${labels[a] ? labels[a] : ''}`
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: clsx(chartStyle.areaChartTooltipLabel, a !== 0 ? chartStyle.left : chartStyle.right),
                    children: `${valueModifiers[b] ? valueModifiers[b](values[b]) : values[b]} ${labels[b] ? labels[b] : ''}`
                })
            ]
        });
    }
    return null;
};
const AreaChart = ({ data, coupleYAxes, chartType, aspect = 21.0 / 9.0, tooltip, labels })=>{
    const [hoverIndex, setHoverIndex] = useState(null);
    const [barChartHoverBackground, setBarChartHoverBackground] = useState(null);
    let yAxisDataLeft = 'y1';
    let yAxisDataRight = 'y2';
    const rightAxisHasData = findIndex(data, (e)=>e.y2 !== null) > -1;
    if (coupleYAxes && rightAxisHasData) {
        let y1 = data.map((i)=>{
            return i.y1;
        });
        let y2 = data.map((i)=>{
            return i.y2;
        });
        if (Math.max(...y2) > Math.max(...y1)) {
            yAxisDataLeft = 'y2';
            yAxisDataRight = 'y2';
        } else {
            yAxisDataLeft = 'y1';
            yAxisDataRight = 'y1';
        }
    }
    let Recharts = useChunk('Recharts');
    if (!Recharts) return null;
    const { ResponsiveContainer, YAxis, XAxis, Tooltip, CartesianGrid, Label, LineChart, Line, BarChart, Bar, Cell } = Recharts;
    const xAxis = /*#__PURE__*/ _jsx(XAxis, {
        dataKey: "x",
        label: {
            value: '',
            position: 'insideBottomRight',
            offset: 0
        },
        dy: 15,
        fontSize: 11,
        angle: -45
    });
    const yAxisLeft = /*#__PURE__*/ _jsx(YAxis, {
        yAxisId: "val1",
        dataKey: yAxisDataLeft,
        fontSize: 'var(--vl_secondaryBody_fontSize)',
        stroke: "var(--vl_accent_color)",
        tickFormatter: labels.valueModifiers[0],
        children: /*#__PURE__*/ _jsx(Label, {
            className: `${chartStyle.areaChartLabel} ${chartStyle.left}`,
            position: "insideLeft",
            value: labels.values[0],
            angle: -90
        })
    });
    const yAxisRight = /*#__PURE__*/ _jsx(YAxis, {
        yAxisId: 'val2',
        dataKey: yAxisDataRight,
        orientation: "right",
        fontSize: 'var(--vl_secondaryBody_fontSize)',
        stroke: "var(--vl_accent_color2)",
        tickFormatter: labels.valueModifiers[1],
        children: /*#__PURE__*/ _jsx(Label, {
            className: `${chartStyle.areaChartLabel} ${chartStyle.right}`,
            position: "insideRight",
            value: labels.values[1],
            angle: 90
        })
    });
    const grid = /*#__PURE__*/ _jsx(CartesianGrid, {
        stroke: "var(--vl_borderColor)",
        strokeDasharray: "3 3"
    });
    const toolTip = /*#__PURE__*/ _jsx(Tooltip, {
        content: /*#__PURE__*/ _jsx(AreaChartTooltip, {
            tooltip: tooltip
        }),
        cursor: {
            fill: 'var(--vl_content_bgColor)'
        }
    });
    let chart = null;
    if (chartType === 'line') {
        chart = /*#__PURE__*/ _jsxs(LineChart, {
            data: data,
            margin: {
                top: 20,
                right: 40,
                bottom: 30,
                left: 20
            },
            children: [
                xAxis,
                yAxisLeft,
                yAxisRight,
                grid,
                toolTip,
                /*#__PURE__*/ _jsx(Line, {
                    type: "monotone",
                    dataKey: "y1",
                    stroke: "var(--vl_accent_color)",
                    yAxisId: "val1",
                    dot: false,
                    strokeWidth: 2
                }),
                /*#__PURE__*/ _jsx(Line, {
                    type: "monotone",
                    dataKey: "y2",
                    stroke: "var(--vl_accent_color2)",
                    yAxisId: "val2",
                    dot: false,
                    strokeWidth: 2
                })
            ]
        });
    } else if (chartType === 'bar') {
        chart = /*#__PURE__*/ _jsxs(BarChart, {
            data: data,
            margin: {
                top: 20,
                right: 40,
                bottom: 30,
                left: 20
            },
            children: [
                xAxis,
                yAxisLeft,
                yAxisRight,
                toolTip,
                grid,
                /*#__PURE__*/ _jsx(Bar, {
                    yAxisId: "val1",
                    dataKey: "y1",
                    barSize: 20,
                    children: data.map((entry, index)=>/*#__PURE__*/ _jsx(Cell, {
                            fill: 'var(--vl_accent_color)'
                        }, `cell-${entry.date}`))
                }),
                /*#__PURE__*/ _jsx(Bar, {
                    yAxisId: "val2",
                    dataKey: "y2",
                    barSize: 20,
                    onMouseEnter: (e, i)=>setHoverIndex(i),
                    onMouseLeave: ()=>setHoverIndex(null),
                    children: data.map((entry, index)=>/*#__PURE__*/ _jsx(Cell, {
                            fill: 'var(--vl_accent_color2)'
                        }, `cell-${entry.date}`))
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(ResponsiveContainer, {
        width: "100%",
        aspect: aspect,
        children: chart
    });
};
export { PieChart, BarChart, AreaChart };
const TooltipCursor = (props)=>{
    let Recharts = useChunk('Recharts');
    if (!Recharts) return null;
    const { x, y, width, height, fill, payloadIndex, onClick, payload } = props;
    return /*#__PURE__*/ _jsx(Recharts.Rectangle, {
        x: x,
        y: y,
        fill: '#EEE7',
        width: width,
        height: height,
        onClick: ()=>onClick(payload[0])
    });
};
export const Histogram = ({ data, dataKey = 'count', range100 = false, onBarClick })=>{
    // renders data = [{x, count: 10}, ...] as barchart histogram
    let Recharts = useChunk('Recharts');
    if (!Recharts) return null;
    let { BarChart, Bar, Tooltip, XAxis, Rectangle } = Recharts;
    let fmt_x = (v)=>`${v} %`;
    let domain = [
        0,
        100
    ];
    if (!range100 && data.length) {
        let xs = data.map((e)=>e.x);
        domain = [
            Math.min(...xs),
            Math.max(...xs)
        ];
    }
    return /*#__PURE__*/ _jsx(AutoSizer, {
        children: /*#__PURE__*/ _jsxs(BarChart, {
            width: 150,
            height: 40,
            data: data,
            children: [
                /*#__PURE__*/ _jsx(Bar, {
                    dataKey: dataKey,
                    fill: 'var(--vl_accent_color)'
                }),
                data.length ? /*#__PURE__*/ _jsx(XAxis, {
                    dataKey: "x",
                    type: 'number',
                    domain: domain,
                    fontSize: 11,
                    tickFormatter: fmt_x,
                    interval: 0,
                    padding: {
                        left: 5,
                        right: 10
                    }
                }) : null,
                /*#__PURE__*/ _jsx(Tooltip, {
                    labelFormatter: fmt_x,
                    cursor: /*#__PURE__*/ _jsx(TooltipCursor, {
                        onClick: ({ payload })=>onBarClick(payload.x)
                    })
                })
            ]
        })
    });
};
